import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Grid, CardContent } from "@mui/material";
import { Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axiosMain from "../http/axiosMain";

const PlanProSx = {
  fontFamily: "Poppins",
  fontWeight: 600,
  fontSize: "20px",
  lineHeight: "100%",
  letterSpacing: "0%",
  verticalAlign: "middle",
  color: "rgba(31, 31, 31, 1)",
};

const BillingCellSx = {
  fontFamily: "Poppins",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "110%",
  letterSpacing: "0%",
  color: "rgba(101, 101, 101, 1)",
};

const PlanPriceSx = {
  fontFamily: "Poppins",
  fontWeight: 600,
  fontSize: "20px",
  lineHeight: "100%",
  letterSpacing: "0%",
  verticalAlign: "middle",
  color: "rgba(107, 142, 118, 1)",
  borderRadius: "8px",
  display: "inline-block",
};

export default function SubScriPtionPlan() {
  const navigate = useNavigate();
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);

  const getButtonText = (planType) => {
    if (planType === "Starter") return "Your Current Plan";
    if (planType === "Plus") return "Upgrade to Plus";
    if (planType === "Pro") return "Talk to Sales";
    return "Subscribe";
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const role = localStorage.getItem("userType");
        const roleId = role === "brand" ? 1 : 2;

        const response = await axiosMain.get(
          `/api/subscription/subscription-plan?role_id=${roleId}`
        );

        if (response.data.status) {
          setSubscriptionPlans(response.data.data);

          // ✅ Use PlanType from localStorage
          const storedPlanType = localStorage.getItem("PlanType");
          if (storedPlanType) {
            const matchedPlan = response.data.data.find(
              (plan) => plan.plan_type === storedPlanType
            );
            if (matchedPlan) {
              setCurrentPlan(matchedPlan);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch subscription plans", error);
      }
    };

    fetchPlans();
  }, []);

  const renderCard = (plan) => {
    const {
      plan_id,
      plan_type,
      discount,
      flag_discount_active,
      monthly_price,
      features,
      description,
    } = plan;

    const hasDiscount = flag_discount_active && discount > 0;
    const original_price = monthly_price;
    const discounted_price = hasDiscount
      ? Math.round(monthly_price - (monthly_price * discount) / 100)
      : monthly_price;

    const isCurrentPlan = currentPlan?.plan_type === plan_type; // ✅ Check active

    return (
      <Box
        sx={{
          px: 3,
          py: 4,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
        }}
        key={plan_id}
      >
        <Typography variant="h6" fontWeight="500">
          {plan_type}
        </Typography>

        <Box display="flex" flexDirection="column" gap={1} mb={2}>
          {hasDiscount && (
            <Typography
              variant="body2"
              sx={{ textDecoration: "line-through", color: "#aaa" }}
            >
              ₹{original_price}
            </Typography>
          )}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h5" fontWeight={700}>
              ₹{discounted_price}
            </Typography>
            {hasDiscount && (
              <Typography
                variant="caption"
                sx={{
                  backgroundColor: "#f5f5f5",
                  color: "#000",
                  fontWeight: 600,
                  px: 1,
                  borderRadius: "4px",
                }}
              >
                -{discount}%
              </Typography>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, mt: 2 }}>
          {features.map((feature, idx) => (
            <Box key={idx} display="flex" gap={1} alignItems="flex-start">
              <Box
                sx={{
                  mt: "2px",
                  width: 16,
                  height: 16,
                  borderRadius: "2px",
                  backgroundColor: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Check size={18} color="#6B8E76" />
              </Box>
              <Typography variant="body2" flex={1}>
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box mt="auto" pt={3}>
          <Button
            fullWidth
            variant="outlined"
            disabled={isCurrentPlan}
            sx={{
              color: isCurrentPlan ? "#1D1B20" : "#6B8E76", // ✅ green for others
              fontWeight: 500,
              borderRadius: "8px",
              textTransform: "none",
              borderColor: isCurrentPlan
                ? "#ccc !important"
                : "#6B8E76 !important", // ✅ green border for others
              "&.Mui-disabled": {
                borderColor: "#ccc !important",
                backgroundColor: "transparent !important",
              },
            }}
          >
            {isCurrentPlan ? "Your Current Plan" : getButtonText(plan_type)}
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box p={3} sx={{ width: "100%", boxShadow: 0 }}>
      <Box sx={{ boxShadow: 0, borderRadius: 2 }}>
        <CardContent sx={{ mt: 3 }}>
          <Box
            sx={{
              border: "1px solid var(--Stroke, rgba(237, 237, 237, 1))",
              p: 3,
              borderRadius: 2,
            }}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            my={2}
          >
            {currentPlan ? (
              <>
                <Box>
                  <Typography sx={PlanProSx}>
                    Plan: {currentPlan.plan_type}
                  </Typography>
                  <Typography className="mt-1" sx={BillingCellSx}>
                    {currentPlan.description}
                  </Typography>
                  <Typography className="mt-2" sx={PlanPriceSx}>
                    ₹
                    {currentPlan.flag_discount_active
                      ? Math.round(
                          currentPlan.monthly_price -
                            (currentPlan.monthly_price * currentPlan.discount) /
                              100
                        )
                      : currentPlan.monthly_price}
                  </Typography>
                </Box>
              </>
            ) : (
              <Typography variant="body1">Loading Current Plan...</Typography>
            )}
          </Box>

          <Grid
            container
            justifyContent="center"
            sx={{
              border: "0.85px solid #e0e0e0",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            {subscriptionPlans.map((plan, index) => (
              <Grid
                item
                xs={12}
                sm={4}
                key={plan.plan_id}
                sx={{
                  borderRight:
                    index !== subscriptionPlans.length - 1
                      ? "0.85px solid #e0e0e0"
                      : "none",
                }}
              >
                {renderCard(plan)}
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Box>
    </Box>
  );
}
